import { compareClientWork } from '../../core/utils/clientWork';
import React, { useMemo, useState } from 'react';
import { Plus, ListChecks, Columns3 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { TaskClientBadges } from './components/TaskClientBadges';
import { PageHeader } from '../../components/shared/PageHeader';
import { LoadingState } from '../../components/shared/LoadingState';
import { ErrorState } from '../../components/shared/ErrorState';
import { EmptyState } from '../../components/shared/EmptyState';
import { DataTable } from '../../components/shared/DataTable';
import { useAuth } from '../../core/contexts/AuthContext';
import { useTasks } from '../../core/hooks/useTasks';
import { useClients } from '../../core/hooks/useClients';
import { useOrganizations } from '../../core/hooks/useOrganizations';
import { useUsers } from '../../core/hooks/useUsers';
import { useTimeEntries } from '../../core/hooks/useTimeEntries';
import { useGithub } from '../../core/hooks/useGithub';
import type { Task } from '../../core/models/Task';
import { TaskPriority, TaskStatus } from '../../core/models/Task';
import { UserRole } from '../../core/models/User';
import { TaskDialog } from './components/TaskDialog';
import { TaskFiltersBar, type TaskFilters } from './components/TaskFiltersBar';
import { compareTaskVersions, formatTaskVersion, parseTaskVersion, parseVersionFromFormSelect } from '../../core/constants/taskVersionMilestone';
import { buildTaskGithubPayload, buildTaskJiraPayload, toDateOrNull } from '../../core/utils/taskForm';

const EMPTY_FILTERS: TaskFilters = {
  clientId: '',
  projectId: '',
  label: '',
  version: '',
  priority: '',
  status: [],
};

export const TasksPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { clientId, currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === UserRole.SUPER_ADMIN;
  const { tasks, loading, error, createTask, updateTask, deleteTask } = useTasks(isSuperAdmin ? null : clientId);
  const { clients, updateClient: updateProject } = useClients(isSuperAdmin ? null : clientId);
  const { organizations } = useOrganizations();
  const { users } = useUsers(isSuperAdmin ? null : clientId, true);
  const { timeEntries } = useTimeEntries({
    organizationId: isSuperAdmin ? undefined : (clientId || undefined),
  });
  const { upsertTaskIssue } = useGithub();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [workQueue, setWorkQueue] = useState('all');
  const [appliedFilters, setAppliedFilters] = useState<TaskFilters>(EMPTY_FILTERS);
  React.useEffect(() => {
    const taskId = searchParams.get('task');
    const linked = tasks.find(task => task.id === taskId);
    if (linked) { setEditingTask(linked); setSearchParams({}, { replace: true }); }
  }, [tasks, searchParams, setSearchParams]);

  const clientAccounts = useMemo(() => {
    const organizationNameById = organizations.reduce((acc, organization) => {
      acc[organization.id] = organization.name;
      if (organization.legacyOrganizationId) {
        acc[organization.legacyOrganizationId] = organization.name;
      }
      return acc;
    }, {} as Record<string, string>);

    const byId = new Map<string, { id: string; name: string }>();
    for (const project of clients) {
      const id = project.clientId || '';
      if (!id) continue;
      if (!byId.has(id)) {
        byId.set(id, { id, name: organizationNameById[id] || id });
      }
    }
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [clients, organizations]);

  const projectById = useMemo(() => {
    return clients.reduce((acc, project) => {
      acc[project.id] = project;
      return acc;
    }, {} as Record<string, (typeof clients)[number]>);
  }, [clients]);

  const singleProjectByClientId = useMemo(() => {
    const grouped = clients.reduce((acc, project) => {
      const cid = project.clientId || '';
      if (!cid) return acc;
      if (!acc[cid]) acc[cid] = [];
      acc[cid].push(project);
      return acc;
    }, {} as Record<string, typeof clients>);

    return Object.entries(grouped).reduce((acc, [cid, projects]) => {
      if (projects.length === 1) {
        acc[cid] = projects[0];
      }
      return acc;
    }, {} as Record<string, (typeof clients)[number]>);
  }, [clients]);

  const resolveProjectForTask = (task: Task) => {
    if (task.projectId && projectById[task.projectId]) {
      return projectById[task.projectId];
    }

    if (task.clientId && singleProjectByClientId[task.clientId]) {
      return singleProjectByClientId[task.clientId];
    }

    if (task.projectId && singleProjectByClientId[task.projectId]) {
      return singleProjectByClientId[task.projectId];
    }

    return undefined;
  };

  const filteredTasks = useMemo(() => {
    return tasks.filter((t) => {
      if (workQueue === 'pending' && (t.status === 'done' || t.clientDecision?.disposition !== 'requested' || (t.workApproval?.status || 'pending') !== 'pending')) return false;
      if (workQueue === 'requested' && (t.status === 'done' || t.clientDecision?.disposition !== 'requested')) return false;
      if (workQueue === 'deferred' && (t.status === 'done' || t.clientDecision?.disposition !== 'deferred')) return false;
      if (appliedFilters.clientId && t.clientId !== appliedFilters.clientId) return false;
      if (appliedFilters.projectId && t.projectId !== appliedFilters.projectId) return false;
      if (appliedFilters.priority && t.priority !== appliedFilters.priority) return false;
      if (appliedFilters.status.length > 0 && !appliedFilters.status.includes(t.status)) return false;
      if (appliedFilters.version && (t.version || '') !== appliedFilters.version) return false;
      if (appliedFilters.label) {
        const selected = appliedFilters.label.toLowerCase();
        const taskLabels = (t.labels || []).map((label) => label.trim().toLowerCase());
        if (!taskLabels.includes(selected)) return false;
      }
      return true;
    }).sort(compareClientWork);
  }, [tasks, appliedFilters, workQueue]);

  const filterLabelOptions = useMemo(() => {
    const labelMap = new Map<string, string>();
    for (const project of clients) {
      for (const rawLabel of project.taskLabels || []) {
        const label = rawLabel.trim();
        if (!label) continue;
        const key = label.toLowerCase();
        if (!labelMap.has(key)) {
          labelMap.set(key, label);
        }
      }
    }
    for (const task of tasks) {
      for (const rawLabel of task.labels || []) {
        const label = rawLabel.trim();
        if (!label) continue;
        const key = label.toLowerCase();
        if (!labelMap.has(key)) {
          labelMap.set(key, label);
        }
      }
    }
    return Array.from(labelMap.values()).sort((a, b) => a.localeCompare(b));
  }, [clients, tasks]);

  const filterVersionOptions = useMemo(() => {
    const versions = new Set<string>();
    for (const project of clients) {
      for (const rawVersion of project.taskVersions || []) {
        const parsed = parseTaskVersion(rawVersion);
        if (parsed) versions.add(parsed);
      }
    }
    for (const task of tasks) {
      if (task.version) {
        versions.add(task.version);
      }
    }
    return Array.from(versions).sort(compareTaskVersions);
  }, [clients, tasks]);

  const roundDurationToQuarterMinutes = (minutes: number) => {
    if (!Number.isFinite(minutes) || minutes <= 0) return 0;
    const rounded = Math.round(minutes / 15) * 15;
    return rounded > 0 ? rounded : 15;
  };

  const actualMinutesByTaskId = useMemo(() => {
    return timeEntries.reduce((acc, entry) => {
      if (!entry.taskId) return acc;
      const rounded = roundDurationToQuarterMinutes(Number(entry.duration) || 0);
      if (!rounded) return acc;
      acc[entry.taskId] = (acc[entry.taskId] || 0) + rounded;
      return acc;
    }, {} as Record<string, number>);
  }, [timeEntries]);

  const dueDateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
    []
  );

  const formatDueDate = (value: unknown): string => {
    const dueDate = toDateOrNull(value);
    return dueDate ? dueDateFormatter.format(dueDate) : 'N/A';
  };

  const formatCreatedDate = (value: unknown): string => {
    const date = toDateOrNull(value);
    return date ? dueDateFormatter.format(date) : '—';
  };

  const formatStatusLabel = (status: TaskStatus): string =>
    status
      .split('-')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');

  const handleCreateTask = async (data: any) => {
    const selectedClientId = data.clientId || clientId;
    if (!selectedClientId) {
      alert('Client account ID is required. Please ensure you are logged in with a valid client context.');
      return;
    }
    if (!data.projectId) {
      alert('Project is required to create a task.');
      return;
    }

    const project = clients.find((c) => c.id === data.projectId);
    const githubPayload = buildTaskGithubPayload(data, project?.githubRepoId);
    const jiraPayload = buildTaskJiraPayload(data);
    const version = parseVersionFromFormSelect(data.version);
    const estimatedHours = data.estimatedHours === '' || data.estimatedHours == null
      ? null
      : Number(data.estimatedHours);
    const taskId = await createTask({
      title: data.title,
      description: data.description || '',
      clientId: selectedClientId,
      projectId: data.projectId,
      status: (data.status || TaskStatus.TODO) as TaskStatus,
      priority: (data.priority || TaskPriority.MEDIUM) as TaskPriority,
      dueDate: toDateOrNull(data.dueDate),
      estimatedHours: Number.isFinite(estimatedHours) ? estimatedHours : null,
      version,
      platform: data.platform && data.platform !== 'none' ? data.platform : null,
      labels: data.labels || [],
      assignedTo: data.assignedTo || [],
      jira: jiraPayload,
      github: githubPayload,
      createdBy: currentUser?.uid,
    });

    const shouldWriteBack = !!(project?.githubSync?.taskWriteBack && project.githubRepoId);
    if (shouldWriteBack) {
      try {
        const issue = await upsertTaskIssue(data.projectId, {
          taskId,
          title: data.title,
          description: data.description || '',
          status: data.status || TaskStatus.TODO,
          priority: data.priority || TaskPriority.MEDIUM,
          dueDate: data.dueDate || null,
          version,
          issueNumber: githubPayload?.issueNumber,
        });

        await updateTask(taskId, {
          github: {
            ...githubPayload,
            repoId: issue.repoId,
            issueId: issue.issueId,
            issueNumber: issue.issueNumber,
            issueTitle: issue.issueTitle,
            issueUrl: issue.issueUrl,
          },
        });
      } catch (error) {
        console.error('GitHub issue write-back failed for created task:', error);
        alert('Task was created, but GitHub issue write-back failed.');
      }
    }
    setIsCreateDialogOpen(false);
  };

  const handleUpdateTask = async (data: any) => {
    if (!editingTask) return;
    if (!data.projectId) {
      alert('Project is required for a task.');
      return;
    }

    const selectedClientId = data.clientId || editingTask.clientId;
    const project = clients.find((c) => c.id === data.projectId);
    const githubPayload = buildTaskGithubPayload(data, project?.githubRepoId);
    const jiraPayload = buildTaskJiraPayload(data);
    const version = parseVersionFromFormSelect(data.version);
    const estimatedHours = data.estimatedHours === '' || data.estimatedHours == null
      ? null
      : Number(data.estimatedHours);
    await updateTask(editingTask.id, {
      clientId: selectedClientId,
      projectId: data.projectId,
      title: data.title,
      description: data.description || '',
      status: data.status as TaskStatus,
      priority: data.priority as TaskPriority,
      dueDate: toDateOrNull(data.dueDate),
      estimatedHours: Number.isFinite(estimatedHours) ? estimatedHours : null,
      version,
      platform: data.platform && data.platform !== 'none' ? data.platform : null,
      labels: data.labels || [],
      assignedTo: data.assignedTo || [],
      jira: jiraPayload,
      github: githubPayload,
    });

    const shouldWriteBack = !!(project?.githubSync?.taskWriteBack && project.githubRepoId);
    if (shouldWriteBack) {
      try {
        const issue = await upsertTaskIssue(data.projectId, {
          taskId: editingTask.id,
          title: data.title,
          description: data.description || '',
          status: data.status,
          priority: data.priority,
          dueDate: data.dueDate || null,
          version,
          issueNumber: githubPayload?.issueNumber || editingTask.github?.issueNumber,
        });

        await updateTask(editingTask.id, {
          github: {
            ...githubPayload,
            repoId: issue.repoId,
            issueId: issue.issueId,
            issueNumber: issue.issueNumber,
            issueTitle: issue.issueTitle,
            issueUrl: issue.issueUrl,
          },
        });
      } catch (error) {
        console.error('GitHub issue write-back failed for updated task:', error);
        alert('Task was updated, but GitHub issue write-back failed.');
      }
    }
    setEditingTask(null);
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm('Delete this task?')) return;
    await deleteTask(taskId);
  };

  const handleAddProjectVersion = async (projectId: string, version: string) => {
    const project = clients.find((c) => c.id === projectId);
    const current = project?.taskVersions || [];
    const next = Array.from(new Set([...current, version])).sort(compareTaskVersions);
    await updateProject(projectId, { taskVersions: next });
  };

  const handleAddProjectLabel = async (projectId: string, label: string) => {
    const project = clients.find((c) => c.id === projectId);
    const current = project?.taskLabels || [];
    const next = Array.from(new Set([...current, label])).sort();
    await updateProject(projectId, { taskLabels: next });
  };

  const getStatusColor = (status: TaskStatus) => {
    switch (status) {
      case TaskStatus.DONE:
        return 'success';
      case TaskStatus.IN_PROGRESS:
        return 'default';
      case TaskStatus.IN_REVIEW:
        return 'warning';
      case TaskStatus.BLOCKED:
        return 'error';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: TaskPriority) => {
    switch (priority) {
      case TaskPriority.URGENT:
        return 'error';
      case TaskPriority.HIGH:
        return 'warning';
      default:
        return 'default';
    }
  };

  const columns: ColumnDef<Task>[] = [
    {
      accessorKey: 'title',
      header: 'Title',
      cell: ({ row }) => <div className="font-medium">{row.original.title}{row.original.clientDecision && <p className="text-xs text-indigo-700">Client {row.original.clientDecision.disposition} · {row.original.clientDecision.priority} priority · Sentral {row.original.workApproval?.status || "pending"}</p>}</div>,
    },
    {
      accessorKey: 'clientId',
      header: 'Client',
      cell: ({ row }) => {
        const cid = row.original.clientId;
        const name = clientAccounts.find((account) => account.id === cid)?.name;
        return <span>{name || cid || 'Unknown client'}</span>;
      },
    },
    {
      accessorKey: 'projectId',
      header: 'Project',
      cell: ({ row }) => {
        const project = resolveProjectForTask(row.original);
        return <span>{project?.name || 'Unknown project'}</span>;
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge
          variant={getStatusColor(row.original.status)}
          className="whitespace-nowrap px-2 py-0.5 leading-none"
        >
          {formatStatusLabel(row.original.status)}
        </Badge>
      ),
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      cell: ({ row }) => (
        <Badge variant={getPriorityColor(row.original.priority)}>{row.original.priority}</Badge>
      ),
    },
    {
      accessorKey: 'platform',
      header: 'Platform',
      cell: ({ row }) => <span>{row.original.platform || '—'}</span>,
    },
    {
      id: 'client',
      header: 'Client says',
      cell: ({ row }) => <TaskClientBadges task={row.original} />,
    },
    {
      accessorKey: 'version',
      header: 'Version',
      cell: ({ row }) => {
        const v = row.original.version;
        return <span>{v != null ? formatTaskVersion(v) : '—'}</span>;
      },
    },
    {
      accessorKey: 'labels',
      header: 'Labels',
      cell: ({ row }) => {
        const labels = row.original.labels;
        if (!labels?.length) return <span className="text-muted-foreground">—</span>;
        return (
          <div className="flex max-w-[14rem] flex-wrap gap-1">
            {labels.map((label) => (
              <Badge key={label} variant="outline" className="whitespace-nowrap px-1.5 py-0 text-xs capitalize">
                {label}
              </Badge>
            ))}
          </div>
        );
      },
    },
    {
      accessorKey: 'dueDate',
      header: 'Due Date',
      cell: ({ row }) => formatDueDate(row.original.dueDate),
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => formatCreatedDate(row.original.createdAt),
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
              onClick={(event) => {
                event.stopPropagation();
                const resolvedProject = resolveProjectForTask(row.original);
                setEditingTask(
                  resolvedProject
                    ? { ...row.original, projectId: resolvedProject.id, clientId: resolvedProject.clientId || row.original.clientId }
                    : row.original
                );
              }}
          >
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(event) => {
              event.stopPropagation();
              handleDeleteTask(row.original.id);
            }}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  if (loading) return <LoadingState message="Loading tasks..." />;
  if (error) return <ErrorState message="Failed to load tasks" />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tasks"
        description="Create, assign, and track work across your team"
        icon={ListChecks}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/tasks/kanban')}>
              <Columns3 className="mr-2 h-4 w-4" />
              Kanban
            </Button>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Task
            </Button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap gap-2">{[['all', 'All work'], ['pending', 'Client requests awaiting approval'], ['requested', 'Client requested'], ['deferred', 'Client deferred']].map(([value, label]) => <Button key={value} variant={workQueue === value ? 'default' : 'outline'} onClick={() => setWorkQueue(value)}>{label}</Button>)}</div>
      {tasks.length === 0 ? (
        <EmptyState
          icon={ListChecks}
          title="No tasks found"
          description="Create your first task to get started"
          action={{
            label: 'Create Task',
            onClick: () => setIsCreateDialogOpen(true),
          }}
        />
      ) : filteredTasks.length === 0 ? (
        <EmptyState
          icon={ListChecks}
          title="No tasks match filters"
          description="Adjust or clear filters to see matching tasks."
        />
      ) : (
        <DataTable
          columns={columns}
          data={filteredTasks}
          searchKey="title"
          searchPlaceholder="Search tasks..."
          toolbarExtra={

      <TaskFiltersBar
        isSuperAdmin={isSuperAdmin}
        clientAccounts={clientAccounts}
        projects={clients.map((c) => ({ id: c.id, name: c.name, clientId: c.clientId || '' }))}
        labels={filterLabelOptions}
        versions={filterVersionOptions}
        filters={appliedFilters}
        onApplyFilters={setAppliedFilters}
        onClearFilters={() => setAppliedFilters(EMPTY_FILTERS)}
      />
          }
          onRowClick={(task) => {
            const resolvedProject = resolveProjectForTask(task);
            setEditingTask(
              resolvedProject
                ? { ...task, projectId: resolvedProject.id, clientId: resolvedProject.clientId || task.clientId }
                : task
            );
          }}
        />
      )}

      <TaskDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        projects={clients.map((c) => ({
          id: c.id,
          name: c.name,
          clientId: c.clientId || '',
          jiraProjectId: c.jiraProjectId,
          taskFieldVisibility: c.taskFieldVisibility,
          taskVersions: c.taskVersions || [],
          platforms: c.platforms || [],
          taskLabels: c.taskLabels || [],
          githubRepoId: c.githubRepoId,
          githubRepoOwner: c.githubRepoOwner,
          githubRepoName: c.githubRepoName,
        }))}
        clientAccounts={clientAccounts}
        users={users}
        isSuperAdmin={isSuperAdmin}
        fixedClientId={clientId}
        onAddProjectVersion={handleAddProjectVersion}
        onAddProjectLabel={handleAddProjectLabel}
        onSubmit={handleCreateTask}
      />

      {editingTask && (
        <TaskDialog
          open={!!editingTask}
          onClose={() => setEditingTask(null)}
          task={editingTask}
          actualMinutes={actualMinutesByTaskId[editingTask.id] || 0}
          projects={clients.map((c) => ({
            id: c.id,
            name: c.name,
            clientId: c.clientId || '',
            jiraProjectId: c.jiraProjectId,
            taskFieldVisibility: c.taskFieldVisibility,
            taskVersions: c.taskVersions || [],
            platforms: c.platforms || [],
            taskLabels: c.taskLabels || [],
            githubRepoId: c.githubRepoId,
            githubRepoOwner: c.githubRepoOwner,
            githubRepoName: c.githubRepoName,
          }))}
          clientAccounts={clientAccounts}
          users={users}
          isSuperAdmin={isSuperAdmin}
          fixedClientId={clientId}
          onAddProjectVersion={handleAddProjectVersion}
          onAddProjectLabel={handleAddProjectLabel}
          onSubmit={handleUpdateTask}
        />
      )}
    </div>
  );
};
